import { ServiceTile, SourceTile } from "../../components/ui/SourceTile";
import { formatDuration } from "../../lib/durations";
import { networkServiceOf } from "../../lib/networkServices";
import { laneOf } from "../../lib/sources";
import { type BlockView, activityTitle } from "./model";
import { type PeekFact, peekActivities, peekRange } from "./peekFacts";

/** What a card's description says, for a screen reader, where it can peek. */
export const PEEK_HINT = "Hold it, with the mouse or Space, to preview it here. Enter opens it.";

/**
 * A card's peek, under what it always shows, while it's held (ADR 0193,
 * decision 6; the prototype's `moreHTML`): its first three activities in
 * time order, each with its icon, title, times and duration, as the popup
 * lists them; "+N more in the popup"; the facts (`peekFacts`); and how to
 * fold it. No buttons: the mouse is still down. Inside the card's button, so
 * only text, in the card's own colours: a peeking card is outlined, not blue
 * (ADR 0194, decision 7). It shows as the card grows, with no fade or slide
 * of its own.
 */
export function PeekDetails({
  blocks,
  facts,
  timeZone,
}: {
  /** The card's blocks: one, or a ticket's (ADR 0189). */
  blocks: readonly BlockView[];
  facts: readonly PeekFact[];
  timeZone: string;
}) {
  const { shown, more } = peekActivities(blocks);
  return (
    <span
      data-part="peek"
      className="flex flex-col gap-2.5 border-t border-line pt-3"
    >
      <span className="flex flex-col gap-1">
        {shown.map((activity) => {
          const block = blocks.find((b) => b.activities.includes(activity)) ?? blocks[0];
          const lane = laneOf(activity.source);
          const service = networkServiceOf(activity);
          return (
            <span
              key={activity.id}
              data-part="peek-activity"
              className="grid grid-cols-[26px_minmax(0,1fr)_auto] items-center gap-2 rounded-row bg-panel-2 py-[5px] pr-2.5 pl-[7px]"
            >
              {service ? (
                <ServiceTile service={service} decorative />
              ) : lane ? (
                <SourceTile lane={lane} decorative />
              ) : (
                <span aria-hidden="true" />
              )}
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-[12.5px] font-semibold">{activityTitle(activity)}</span>
                <span className="font-mono text-[11px] text-muted">
                  {block === undefined ? null : peekRange(activity, block, timeZone)}
                </span>
              </span>
              <span className="text-[13px] font-semibold whitespace-nowrap">
                {formatDuration(activity.counted_seconds)}
              </span>
            </span>
          );
        })}
      </span>
      {more > 0 ? <span className="text-xs text-muted">{`+${String(more)} more in the popup`}</span> : null}
      {facts.length > 0 ? (
        <span className="flex flex-col gap-[5px] text-[12.5px] leading-[1.4]">
          {facts.map((fact) => (
            <span key={fact.label} data-part="peek-fact" className="grid grid-cols-[86px_minmax(0,1fr)] gap-2.5">
              <span className="text-muted">
                {fact.label}
                <span className="sr-only">: </span>
              </span>
              <span
                className={
                  "[overflow-wrap:anywhere]" +
                  (fact.withheld === true ? " text-muted italic" : "") +
                  (fact.clamp === true ? " line-clamp-3" : "")
                }
              >
                {fact.value}
              </span>
            </span>
          ))}
        </span>
      ) : null}
      <span className="text-xs text-muted">Let go to fold it. A click opens it.</span>
    </span>
  );
}
